import { useState, useEffect, useMemo } from 'react';
import AdminHeader from '../components/AdminHeader';
import { useNavigate } from 'react-router-dom';
import {
  ChakraProvider,
  useToast,
  Box,
  Container,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Input,
  InputGroup,
  InputLeftElement,
  Select,
  Button,
  HStack,
  VStack,
  Text,
  Badge,
  Flex,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  ModalFooter,
  Spinner,
  Center,
  Card,
  CardBody,
} from '@chakra-ui/react';

function ContactListings() {
  const [contacts, setContacts] = useState([]);
  const [filteredContacts, setFilteredContacts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterBy, setFilterBy] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedContact, setSelectedContact] = useState(null);
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [pagination, setPagination] = useState({
    total_records: 0,
    total_pages: 0,
    current_page: 1,
    per_page: 20,
    has_next_page: false,
    has_prev_page: false
  });
  const { isOpen, onOpen, onClose } = useDisclosure();
  const itemsPerPage = 20;
  const navigate = useNavigate();
  const toast = useToast();

  useEffect(() => {
    // Check if user is authenticated
    const token = localStorage.getItem('adminToken');
    if (!token) {
      navigate('/Admin');
      return;
    }

    fetchContacts();
    
    // Refresh data when window gains focus (user returns to tab)
    const handleFocus = () => {
      if (token) {
        fetchContacts();
      }
    };
    
    window.addEventListener('focus', handleFocus);
    
    return () => {
      window.removeEventListener('focus', handleFocus);
    };
  }, [navigate]);

  useEffect(() => {
    // Fetch contacts when page, search, or filter changes
    const token = localStorage.getItem('adminToken');
    if (token) {
      fetchContacts();
    }
  }, [currentPage, searchTerm, filterBy]);

  useEffect(() => {
    // Show search results dropdown after 3 characters
    if (searchTerm.trim().length >= 3) {
      setShowSearchResults(true);
    } else {
      setShowSearchResults(false);
    }
  }, [searchTerm]);

  // Memoized search results for dropdown
  const searchResults = useMemo(() => {
    if (searchTerm.trim().length < 3) return [];
    
    const search = searchTerm.toLowerCase();
    return contacts
      .filter(
        (contact) =>
          (contact.name && contact.name.toLowerCase().includes(search)) ||
          (contact.email && contact.email.toLowerCase().includes(search)) ||
          (contact.phone_number && contact.phone_number.includes(search)) ||
          (contact.query_description && contact.query_description.toLowerCase().includes(search))
      )
      .slice(0, 5); // Show max 5 results in dropdown
  }, [contacts, searchTerm]);

  const fetchContacts = async () => {
    setIsLoading(true);
    try {
      // Build query parameters
      const params = new URLSearchParams({
        page: currentPage.toString(),
        limit: itemsPerPage.toString()
      });
      
      // Add search parameter if search term exists (for backend search)
      if (searchTerm.trim().length >= 3) {
        params.append('search', searchTerm.trim());
      }
      
      // Add filter parameter if filter is active
      if (filterBy !== 'all') {
        params.append('filter', filterBy);
      }

      // Add cache-busting parameter to ensure fresh data
      params.append('_t', Date.now().toString());
      
      const response = await fetch(
        `https://myblrc.in/api/api.php?xAction=getContactUs&${params.toString()}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'Cache-Control': 'no-cache',
            'Pragma': 'no-cache',
          },
          cache: 'no-store', // Prevent browser caching
        }
      );

      // Check if response is OK
      if (!response.ok) {
        const errorText = await response.text();
        console.error('API Error Response:', errorText);
        throw new Error(`API returned status ${response.status}: ${errorText}`);
      }

      const result = await response.json();
      
      // Check if API returned an error
      if (result.status === 'error' || result.data?.status === 'error') {
        console.error('API Error:', result);
        throw new Error(result.data?.message || result.message || 'API returned an error');
      }

      let contactsData = [];
      let paginationData = null;
      
      if (result.data?.status === 'success' && result.data?.contacts) {
        contactsData = result.data.contacts;
        paginationData = result.data?.pagination;
      } else if (result.status === 'success' && result.contacts) {
        contactsData = result.contacts;
        paginationData = result.pagination;
      } else if (result.data && Array.isArray(result.data)) {
        contactsData = result.data;
      } else if (Array.isArray(result)) {
        contactsData = result;
      }
      
      console.log('Total contacts fetched:', contactsData.length);
      console.log('Contacts data:', contactsData);
      console.log('Pagination data:', paginationData);
      
      setContacts(contactsData);
      
      // Update pagination state if available
      if (paginationData) {
        setPagination({
          total_records: paginationData.total_records || 0,
          total_pages: paginationData.total_pages || 0,
          current_page: paginationData.current_page || currentPage,
          per_page: paginationData.per_page || itemsPerPage,
          has_next_page: paginationData.has_next_page || false,
          has_prev_page: paginationData.has_prev_page || false
        });
      }
      
      if (contactsData.length === 0 && currentPage === 1) {
        toast({
          description: 'No contacts found',
          status: 'info',
          duration: 3000,
          isClosable: true,
          position: 'bottom',
        });
      }
    } catch (error) {
      console.error('Failed to fetch contacts:', error);
      console.error('Error details:', {
        message: error.message,
        stack: error.stack,
        name: error.name
      });
      toast({
        description: error.message || 'Failed to fetch contacts. Please check your internet connection and try again.',
        status: 'error',
        duration: 5000,
        isClosable: true,
        position: 'bottom',
      });
      setContacts([]);
    } finally {
      setIsLoading(false);
    }
  };

  // Client-side filtering for search dropdown only (server handles main filtering)
  const applyFilters = () => {
    // This is now only used for the search dropdown suggestions
    setFilteredContacts(contacts);
  };

  const handleViewDetails = (contact) => {
    setSelectedContact(contact);
    onOpen();
  };

  // Use server-side pagination data
  const totalPages = pagination.total_pages || 1;
  const currentContacts = contacts; // Contacts are already paginated from server
  
  console.log('Display info:', {
    totalContacts: pagination.total_records,
    currentPage: pagination.current_page,
    itemsPerPage: pagination.per_page,
    currentContactsCount: contacts.length,
    totalPages: pagination.total_pages,
    hasNextPage: pagination.has_next_page,
    hasPrevPage: pagination.has_prev_page
  });

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  // Handle search and filter changes - reset to page 1
  const handleSearchChange = (value) => {
    setSearchTerm(value);
    setCurrentPage(1);
  };

  const handleFilterChange = (value) => {
    setFilterBy(value);
    setCurrentPage(1);
  };

  if (isLoading) {
    return (
      <ChakraProvider>
        <Center minH="100vh">
          <VStack spacing={4}>
            <Spinner size="xl" color="blue.500" />
            <Text>Loading contacts...</Text>
          </VStack>
        </Center>
      </ChakraProvider>
    );
  }

  return (
    <ChakraProvider>
      <Box minH="100vh" bg="gray.50">
        <AdminHeader
          activeTab="contacts"
          title="Contact Form Submissions"
          badge={
            <Badge colorScheme="blue" fontSize="sm" px={3} py={1} borderRadius="full">
              {pagination.total_records} Total
            </Badge>
          }
        />

        <Container maxW="7xl" pb={8}>
          <VStack spacing={6} align="stretch">
            {/* Search and Filter Section */}
            <Card borderRadius="lg" boxShadow="md" border="1px solid" borderColor="gray.200">
              <CardBody p={6}>
                <VStack spacing={4} align="stretch">
                  <HStack spacing={4} wrap="wrap">
                    <Box flex="1" minW="300px" position="relative">
                      <InputGroup>
                        <InputLeftElement pointerEvents="none" color="gray.400">
                          🔍
                        </InputLeftElement>
                        <Input
                          placeholder="Search by name, email, phone, or query... (min 3 chars)"
                          value={searchTerm}
                          onChange={(e) => handleSearchChange(e.target.value)}
                          size="lg"
                          borderRadius="md"
                          border="2px solid"
                          borderColor="gray.200"
                          _focus={{ borderColor: 'blue.500', boxShadow: '0 0 0 1px #3182ce' }}
                        />
                      </InputGroup>
                      
                      {/* Search Results Dropdown */}
                      {showSearchResults && searchResults.length > 0 && (
                        <Box
                          position="absolute"
                          top="100%"
                          left={0}
                          right={0}
                          mt={1}
                          bg="white"
                          border="1px solid"
                          borderColor="gray.200"
                          borderRadius="md"
                          boxShadow="lg"
                          zIndex={10}
                          maxH="300px"
                          overflowY="auto"
                        >
                          {searchResults.map((contact, idx) => (
                            <Box
                              key={contact.id || idx}
                              p={3}
                              borderBottom="1px solid"
                              borderColor="gray.100"
                              cursor="pointer"
                              _hover={{ bg: 'blue.50' }}
                              onClick={() => {
                                setSearchTerm(contact.name || contact.email || '');
                                setShowSearchResults(false);
                              }}
                            >
                              <Text fontWeight="600" fontSize="sm" color="gray.800">
                                {contact.name || 'N/A'}
                              </Text>
                              <Text fontSize="xs" color="gray.500">
                                {contact.email || contact.phone_number || 'N/A'}
                              </Text>
                            </Box>
                          ))}
                        </Box>
                      )}
                    </Box>
                    
                    <Select
                      value={filterBy}
                      onChange={(e) => handleFilterChange(e.target.value)}
                      maxW="250px"
                      size="lg"
                      borderRadius="md"
                      border="2px solid"
                      borderColor="gray.200"
                      _focus={{ borderColor: 'blue.500', boxShadow: '0 0 0 1px #3182ce' }}
                    >
                      <option value="all">All Contacts</option>
                      <option value="7days">Last 7 Days</option>
                      <option value="15days">Last 15 Days</option>
                      <option value="1month">Last 1 Month</option>
                      <option value="3months">Last 3 Months</option>
                      <option value="6months">Last 6 Months</option>
                      <option value="1year">Last 1 Year</option>
                    </Select>
                    
                    <Button
                      colorScheme="blue"
                      onClick={() => {
                        setCurrentPage(1); // Reset to first page
                        fetchContacts();
                      }}
                      isLoading={isLoading}
                      size="lg"
                      borderRadius="md"
                      fontWeight="600"
                      px={6}
                    >
                      🔄 Refresh
                    </Button>
                  </HStack>
                  
                  {/* Results Count */}
                  <Flex justify="space-between" align="center" pt={2} wrap="wrap" gap={2}>
                    <VStack spacing={1} align="start">
                      <Text fontSize="sm" color="gray.600" fontWeight="500">
                        Showing <strong>{currentContacts.length}</strong> contact(s) on page <strong>{pagination.current_page}</strong> of <strong>{pagination.total_pages}</strong>
                        {pagination.total_records > 0 && (
                          <Text as="span" color="gray.400" fontSize="xs" ml={2}>
                            (Total: {pagination.total_records})
                          </Text>
                        )}
                      </Text>
                      {filterBy !== 'all' && (
                        <Text fontSize="xs" color="blue.600" fontWeight="500">
                          📅 Filtered by time range
                        </Text>
                      )}
                      {searchTerm.trim().length >= 3 && (
                        <Text fontSize="xs" color="blue.600" fontWeight="500">
                          🔍 Searching: "{searchTerm}"
                        </Text>
                      )}
                    </VStack>
                    {filterBy !== 'all' && (
                      <Badge colorScheme="purple" fontSize="xs" px={2} py={1} borderRadius="full">
                        Filtered
                      </Badge>
                    )}
                  </Flex>
                </VStack>
              </CardBody>
            </Card>

            {/* Table */}
            <Card borderRadius="lg" boxShadow="md" border="1px solid" borderColor="gray.200" overflow="hidden">
              <Box overflowX="auto">
                <Table variant="simple" size="md">
                  <Thead bg="blue.50">
                    <Tr>
                      <Th color="gray.700" fontWeight="700" fontSize="sm" py={4}>ID</Th>
                      <Th color="gray.700" fontWeight="700" fontSize="sm">Name</Th>
                      <Th color="gray.700" fontWeight="700" fontSize="sm">Email</Th>
                      <Th color="gray.700" fontWeight="700" fontSize="sm">Phone</Th>
                      <Th color="gray.700" fontWeight="700" fontSize="sm">Query</Th>
                      <Th color="gray.700" fontWeight="700" fontSize="sm">Date</Th>
                      <Th color="gray.700" fontWeight="700" fontSize="sm" textAlign="center">Actions</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {currentContacts.length === 0 ? (
                      <Tr>
                        <Td colSpan={7} textAlign="center" py={12}>
                          <VStack spacing={2}>
                            <Text fontSize="lg" color="gray.500" fontWeight="500">
                              No contacts found
                            </Text>
                            <Text fontSize="sm" color="gray.400">
                              {searchTerm ? 'Try adjusting your search or filters' : 'No contact submissions yet'}
                            </Text>
                          </VStack>
                        </Td>
                      </Tr>
                    ) : (
                      currentContacts.map((contact, index) => (
                        <Tr
                          key={contact.id || index}
                          _hover={{ bg: 'blue.50', transform: 'scale(1.01)' }}
                          transition="all 0.2s"
                          borderBottom="1px solid"
                          borderColor="gray.100"
                        >
                          <Td fontWeight="600" color="gray.600">
                            {contact.id || ((pagination.current_page - 1) * itemsPerPage + index + 1)}
                          </Td>
                          <Td fontWeight="600" color="gray.800">
                            {contact.name || 'N/A'}
                          </Td>
                          <Td color="gray.700">{contact.email || 'N/A'}</Td>
                          <Td color="gray.700">{contact.phone_number || contact.phone || 'N/A'}</Td>
                          <Td maxW="250px" isTruncated color="gray.600">
                            {contact.query_description || contact.message || 'N/A'}
                          </Td>
                          <Td color="gray.600" fontSize="sm">
                            {contact.created_at
                              ? new Date(contact.created_at).toLocaleDateString('en-US', {
                                  year: 'numeric',
                                  month: 'short',
                                  day: 'numeric'
                                })
                              : 'N/A'}
                          </Td>
                          <Td textAlign="center">
                            <Button
                              size="sm"
                              colorScheme="blue"
                              onClick={() => handleViewDetails(contact)}
                              borderRadius="md"
                              fontWeight="600"
                              _hover={{ transform: 'translateY(-2px)', boxShadow: 'md' }}
                              transition="all 0.2s"
                            >
                              View
                            </Button>
                          </Td>
                        </Tr>
                      ))
                    )}
                  </Tbody>
                </Table>
              </Box>
            </Card>

            {/* Pagination */}
            {totalPages > 1 && (
              <Card borderRadius="lg" boxShadow="sm" border="1px solid" borderColor="gray.200">
                <CardBody p={4}>
                  <VStack spacing={3}>
                    <Flex justify="center" align="center" gap={2} wrap="wrap">
                      <Button
                        onClick={() => handlePageChange(pagination.current_page - 1)}
                        isDisabled={!pagination.has_prev_page || isLoading}
                        aria-label="Previous page"
                        size="md"
                        borderRadius="md"
                        fontWeight="600"
                        _hover={{ transform: 'translateY(-2px)' }}
                        transition="all 0.2s"
                      >
                        ‹ Prev
                      </Button>
                      <HStack spacing={2}>
                        {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => {
                          if (
                            page === 1 ||
                            page === totalPages ||
                            (page >= pagination.current_page - 1 && page <= pagination.current_page + 1)
                          ) {
                            return (
                              <Button
                                key={page}
                                size="md"
                                colorScheme={pagination.current_page === page ? 'blue' : 'gray'}
                                onClick={() => handlePageChange(page)}
                                borderRadius="md"
                                fontWeight="600"
                                minW="40px"
                                isDisabled={isLoading}
                                _hover={{ transform: 'translateY(-2px)' }}
                                transition="all 0.2s"
                              >
                                {page}
                              </Button>
                            );
                          } else if (page === pagination.current_page - 2 || page === pagination.current_page + 2) {
                            return <Text key={page} px={2}>...</Text>;
                          }
                          return null;
                        })}
                      </HStack>
                      <Button
                        onClick={() => handlePageChange(pagination.current_page + 1)}
                        isDisabled={!pagination.has_next_page || isLoading}
                        aria-label="Next page"
                        size="md"
                        borderRadius="md"
                        fontWeight="600"
                        _hover={{ transform: 'translateY(-2px)' }}
                        transition="all 0.2s"
                      >
                        Next ›
                      </Button>
                    </Flex>
                    <Text fontSize="xs" color="gray.500">
                      Page {pagination.current_page} of {pagination.total_pages} • {pagination.total_records} total records
                    </Text>
                  </VStack>
                </CardBody>
              </Card>
            )}
          </VStack>
        </Container>

        {/* Detail Modal */}
        <Modal isOpen={isOpen} onClose={onClose} size="lg">
          <ModalOverlay bg="blackAlpha.600" />
          <ModalContent borderRadius="xl" boxShadow="2xl">
            <ModalHeader
              bg="blue.50"
              borderTopRadius="xl"
              borderBottom="1px solid"
              borderColor="gray.200"
              fontWeight="700"
              fontSize="xl"
              color="gray.800"
            >
              Contact Details
            </ModalHeader>
            <ModalCloseButton />
            <ModalBody p={6}>
              {selectedContact && (
                <VStack spacing={5} align="stretch">
                  <Box
                    p={4}
                    bg="gray.50"
                    borderRadius="md"
                    border="1px solid"
                    borderColor="gray.200"
                  >
                    <Text fontWeight="700" color="gray.600" fontSize="sm" mb={2}>
                      ID
                    </Text>
                    <Text fontSize="lg" fontWeight="600" color="gray.800">
                      {selectedContact.id || 'N/A'}
                    </Text>
                  </Box>
                  
                  <Box
                    p={4}
                    bg="gray.50"
                    borderRadius="md"
                    border="1px solid"
                    borderColor="gray.200"
                  >
                    <Text fontWeight="700" color="gray.600" fontSize="sm" mb={2}>
                      Name
                    </Text>
                    <Text fontSize="lg" color="gray.800">
                      {selectedContact.name || 'N/A'}
                    </Text>
                  </Box>
                  
                  <Box
                    p={4}
                    bg="gray.50"
                    borderRadius="md"
                    border="1px solid"
                    borderColor="gray.200"
                  >
                    <Text fontWeight="700" color="gray.600" fontSize="sm" mb={2}>
                      Email
                    </Text>
                    <Text fontSize="lg" color="blue.600">
                      {selectedContact.email || 'N/A'}
                    </Text>
                  </Box>
                  
                  <Box
                    p={4}
                    bg="gray.50"
                    borderRadius="md"
                    border="1px solid"
                    borderColor="gray.200"
                  >
                    <Text fontWeight="700" color="gray.600" fontSize="sm" mb={2}>
                      Phone
                    </Text>
                    <Text fontSize="lg" color="gray.800">
                      {selectedContact.phone_number || selectedContact.phone || 'N/A'}
                    </Text>
                  </Box>
                  
                  <Box
                    p={4}
                    bg="gray.50"
                    borderRadius="md"
                    border="1px solid"
                    borderColor="gray.200"
                  >
                    <Text fontWeight="700" color="gray.600" fontSize="sm" mb={2}>
                      Query Description
                    </Text>
                    <Text whiteSpace="pre-wrap" color="gray.800" lineHeight="1.6">
                      {selectedContact.query_description || selectedContact.message || 'N/A'}
                    </Text>
                  </Box>
                  
                  <Box
                    p={4}
                    bg="blue.50"
                    borderRadius="md"
                    border="1px solid"
                    borderColor="blue.200"
                  >
                    <Text fontWeight="700" color="blue.700" fontSize="sm" mb={2}>
                      Submitted Date
                    </Text>
                    <Text fontSize="lg" color="blue.800" fontWeight="600">
                      {selectedContact.created_at
                        ? new Date(selectedContact.created_at).toLocaleString('en-US', {
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          })
                        : 'N/A'}
                    </Text>
                  </Box>
                </VStack>
              )}
            </ModalBody>
            <ModalFooter borderTop="1px solid" borderColor="gray.200">
              <Button
                onClick={onClose}
                colorScheme="blue"
                borderRadius="md"
                fontWeight="600"
                px={6}
              >
                Close
              </Button>
            </ModalFooter>
          </ModalContent>
        </Modal>
      </Box>
    </ChakraProvider>
  );
}

export default ContactListings;

